import { useMemo, useState } from "react";
import { CourseSet } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { ProgramInfo } from "../../types/program.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { DEFAULT_SEMESTER } from "../../constants";
import { getCoursesFromCourseSet, getCourseSetMultiRequirementLabel } from "../../utils/requirementResponseUtils";
import { sortCoursesById } from "../../utils/sortUtils";
import { useHydratedCourses } from "../../hooks/useHydratedCourses";
import CourseRow from "../course-row/CourseRow";
import { CourseRowSkeletonRows } from "../loading/CourseRowSkeleton";
import CircularCheckmark from "../icons/CircularCheckmark";

interface CourseSetRowsProps {
    courseSet: CourseSet;
    programInfo: ProgramInfo;
    coursesMap: Record<string, CourseSummary>;
    /** false = 切片未到,按 requiredCourseIds 数量渲染骨架行防 CLS */
    isSliceLoaded: boolean;
    requirementId: string;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    hideUnavailable: boolean;
    showHeader?: boolean;
    emptyMessage?: string;
}

export default function CourseSetRows({
    courseSet,
    programInfo,
    coursesMap,
    isSliceLoaded,
    requirementId,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    optimisticFavorites,
    userCourses,
    hideUnavailable,
    showHeader,
    emptyMessage,
}: CourseSetRowsProps) {
    const [showExams, setShowExams] = useState(false);

    const { nonExamCourses, examCourses } = useMemo(() => {
        const sorted = sortCoursesById(
            getCoursesFromCourseSet(courseSet, coursesMap, programInfo.relevantSubjects, {
                includeCompletedRepresentative: true,
            })
        );
        const filtered = hideUnavailable ? sorted.filter(c => c.courseState.isSemesterAvailable) : sorted;
        return {
            nonExamCourses: filtered.filter(c => c.type !== 'EXAM'),
            examCourses: filtered.filter(c => c.type === 'EXAM'),
        };
    }, [courseSet, coursesMap, programInfo.relevantSubjects, hideUnavailable]);

    // 补水边界:摘要(用户态)+ catalog(展示数据)合成完整 CourseOption;
    // 未到位的 key 渲染骨架行
    const visibleCourses = useMemo(
        () => (showExams ? [...nonExamCourses, ...examCourses] : nonExamCourses),
        [nonExamCourses, examCourses, showExams]
    );
    const hydrated = useHydratedCourses(visibleCourses);

    const multiReqLabel = getCourseSetMultiRequirementLabel(courseSet);

    const renderRow = (summary: CourseSummary) => (
        <CourseRow
            key={summary.mapKey}
            course={summary}
            hydrated={hydrated.get(summary.mapKey)}
            selectedSemester={DEFAULT_SEMESTER}
            onAddToSchedule={(course, classSectionIds) => handleAddCourse(course, [], DEFAULT_SEMESTER, classSectionIds)}
            onMarkAsTaken={(course) => handleAddCourse(course, [], null)}
            onDeleteFromSchedule={(course) => handleDeleteCourse(course)}
            onFavoriteToggle={(course) => handleFavoriteCourse(course)}
            optimisticFavorites={optimisticFavorites}
            userCourses={userCourses}
            requirementId={requirementId}
        />
    );

    return (
        <>
            {showHeader && (
                <tr className="bg-indigo-50/50">
                    <td colSpan={4} className="px-4 pt-2.5 pb-0.5 text-xs md:text-sm tracking-wide text-black border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            {courseSet.title && <span className="font-medium">{courseSet.title}</span>}
                            {multiReqLabel && <span className="text-xs font-normal text-gray-500">{multiReqLabel}</span>}
                            {courseSet.summary.isFulfilled && <CircularCheckmark className="h-5 w-5 text-green-600" />}
                        </div>
                    </td>
                </tr>
            )}
            {!isSliceLoaded ? (
                <CourseRowSkeletonRows rows={Math.min(Math.max(courseSet.requiredCourseIds.length, 1), 5)} />
            ) : nonExamCourses.length === 0 && examCourses.length === 0 ? (
                emptyMessage ? (
                    <tr>
                        <td colSpan={4} className="px-4 py-4 text-sm text-gray-400 italic border-b">
                            {emptyMessage}
                        </td>
                    </tr>
                ) : null
            ) : (
                <>
                    {nonExamCourses.map(renderRow)}
                    {examCourses.length > 0 && !showExams && (
                        <tr>
                            <td colSpan={4} className="px-4 py-2 border-b border-gray-50">
                                <button
                                    onClick={() => setShowExams(true)}
                                    className="text-xs font-medium text-gray-500 hover:text-gray-600 hover:underline transition-all"
                                >
                                    + Show {examCourses.length} exam{examCourses.length > 1 ? 's' : ''} that fulfill this requirement
                                </button>
                            </td>
                        </tr>
                    )}
                    {showExams && examCourses.map(renderRow)}
                </>
            )}
        </>
    );
}
